import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { faPlus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Chip, Menu, MenuItem, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Editor, { RichTextEditorHandle, TokenOption } from '@scoir/editor'
import { Tooltip } from '@scoir/tooltips'
import { get, isEmpty, size, truncate, uniqueId } from 'lodash'
import { useSelector } from 'react-redux'

import sanitize from 'common/sanitize-html-helper'
import { LinkField } from 'components/LinkField/MuiLinkField'
import MessageDetails from 'routes/outreach/OutreachMessages/PreviewModal/MessageDetails'

import TokenValidationFeedback from './TokenValidationFeedback'
import { extractTokens, isValidToken } from './token-highlighting'

const MAX_LINKLABEL_LENGTH = 100

const BODY_TOKENS: TokenOption[] = [{ label: 'Preferred Name', value: 'Preferred_Name' }]

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
  charsRemaining: {
    textAlign: 'right' as const,
    marginTop: theme.spacing(0.5),
  },
  chip: {
    '& .MuiChip-icon': {
      height: theme.spacing(1.5),
      width: theme.spacing(1.5),
      marginLeft: theme.spacing(1),
    },
  },
}))

// ─── CharactersRemaining ──────────────────────────────────────────────────────

interface CharactersRemainingProps {
  textLength: number
  maxChars: number
}

const CharactersRemaining = ({ textLength, maxChars }: CharactersRemainingProps) => {
  const classes = useStyles()
  const remaining = maxChars - textLength
  return (
    <Typography
      variant="caption"
      color={remaining < 0 ? 'error' : 'textSecondary'}
      className={classes.charsRemaining}
      display="block"
    >
      {remaining} characters remaining
    </Typography>
  )
}

// ─── MessageContent ───────────────────────────────────────────────────────────

interface Message {
  Subject?: string
  Message?: string
  Link?: string
  LinkLabel?: string
}

interface MessageContentProps {
  message: Message
  onChange: (field: string, value: string) => void
  showPersonalizationButton?: boolean
}

const MessageContent = ({ message, onChange, showPersonalizationButton = false }: MessageContentProps) => {
  const uId = useMemo(() => uniqueId(), [])
  const college = useSelector((state: any) => state.college.record)

  const editorRef = useRef<RichTextEditorHandle>(null)
  const subjectInputRef = useRef<HTMLInputElement>(null)
  const pendingSubjectCursor = useRef<number | null>(null)

  // Restore subject cursor after the controlled value re-renders.
  useEffect(() => {
    if (pendingSubjectCursor.current !== null && subjectInputRef.current) {
      const pos = pendingSubjectCursor.current
      subjectInputRef.current.setSelectionRange(pos, pos)
      pendingSubjectCursor.current = null
    }
  })

  const insertSubjectToken = useCallback(
    (tokenName: string) => {
      const token = `{{${tokenName}}}`
      const input = subjectInputRef.current
      const subject = get(message, 'Subject', '')
      const start = input?.selectionStart ?? subject.length
      const end = input?.selectionEnd ?? start
      const newSubject = subject.slice(0, start) + token + subject.slice(end)
      pendingSubjectCursor.current = start + token.length
      onChange('Subject', newSubject)
    },
    [message, onChange],
  )

  const updateLinkLabel = useCallback(
    (value: string) => {
      const nextValue =
        size(value) > MAX_LINKLABEL_LENGTH ? truncate(value, { length: MAX_LINKLABEL_LENGTH, omission: '' }) : value
      onChange('LinkLabel', nextValue)
    },
    [onChange],
  )

  const subjectTokens = extractTokens(get(message, 'Subject', ''))
  const hasSubjectError = subjectTokens.some((t) => !isValidToken(t))
  const [preview, showPreview] = useState(false)

  return (
    <div>
      <Button
        variant="contained"
        color="primary"
        onClick={() => showPreview((prev) => !prev)}
        disabled={!message.Subject || !message.Message || message.Message === '<p></p>'}
      >
        {preview ? 'Edit Message' : 'Preview Message'}
      </Button>
      {preview ? (
        <Box mt={2}>
          <MessageDetails
            // New messages do not have college information until they are saved
            // spread college info into message for preview to ensure it shows in the preview
            message={{ ...college, CollegeName: college.Name, ...message }}
            inEditMode
          />
        </Box>
      ) : (
        <>
          <Box mb={1}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              gridGap={8}
            >
              <TextField
                id={`outreach-message-subject-${uId}`}
                margin="normal"
                fullWidth
                variant="outlined"
                label="Subject"
                value={get(message, 'Subject', '')}
                onChange={(e) => onChange('Subject', e.target.value)}
                error={hasSubjectError}
                inputRef={subjectInputRef}
                InputProps={{
                  endAdornment: (
                    <AddTokenPersonalization
                      insertSubjectToken={insertSubjectToken}
                      showPersonalizationButton={showPersonalizationButton}
                    />
                  ),
                }}
              />
            </Box>
            <TokenValidationFeedback tokens={subjectTokens} />
          </Box>

          <Box>
            <Box mb={1}>
              <Typography component="span">
                <strong>Body</strong>
              </Typography>
            </Box>

            <Editor
              ref={editorRef}
              value={get(message, 'Message', '')}
              onChange={(html) => onChange('Message', sanitize(html))}
              ariaLabel="Message body"
              tokens={showPersonalizationButton ? BODY_TOKENS : []}
              allowLists
            />
          </Box>

          <Box mt={3}>
            <Typography paragraph>
              <strong>Add Call to Action (Optional)</strong>
            </Typography>
            <LinkField
              label="Button Link URL"
              url={get(message, 'Link', '')}
              onChange={(url: string) => onChange('Link', url)}
            />
            {!isEmpty(get(message, 'Link')) && (
              <React.Fragment>
                <TextField
                  id={`outreach-message-link-label-${uId}`}
                  margin="normal"
                  fullWidth
                  variant="outlined"
                  value={get(message, 'LinkLabel', '')}
                  onChange={(e) => updateLinkLabel(e.target.value)}
                  label="Button Link Label"
                />
                <CharactersRemaining
                  textLength={size(get(message, 'LinkLabel', ''))}
                  maxChars={MAX_LINKLABEL_LENGTH}
                />
              </React.Fragment>
            )}
          </Box>
        </>
      )}
    </div>
  )
}

const AddTokenPersonalization = ({
  insertSubjectToken,
  showPersonalizationButton,
}: {
  insertSubjectToken: (token: string) => void
  showPersonalizationButton: boolean
}) => {
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleTokenMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleTokenMenuClose = () => {
    setAnchorEl(null)
  }
  if (!showPersonalizationButton) return null
  return (
    <>
      <Tooltip title="Insert personalization token">
        <Chip
          key="Preferred_Name"
          label="Personalize"
          className={classes.chip}
          icon={<FontAwesomeIcon icon={faPlus} />}
          size="small"
          onClick={handleTokenMenuClick}
          color="primary"
          variant="outlined"
        />
      </Tooltip>
      <Menu
        id="token-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleTokenMenuClose}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={() => {
            insertSubjectToken('Preferred_Name')
            handleTokenMenuClose()
          }}
        >
          Preferred Name
        </MenuItem>
      </Menu>
    </>
  )
}

export default MessageContent
